import { AxiosResponse } from 'axios';
import { getApi } from 'api';
import { fromLoginResponseToToken } from 'mappers/auth-mapper';
import {
  MFA_ENROLL_CONFIRM_ENDPOINT,
  MFA_ENROLL_ENDPOINT,
  MFA_OPT_IN_ENDPOINT,
  MFA_VERIFY_BACKUP_CODE_ENDPOINT,
  MFA_VERIFY_ENDPOINT,
} from 'constants/endpoints';
import ILoginResponse from 'types/auth/ILoginResponse';
import IMfaBackupCodeVerifyRequest from 'types/auth/IMfaBackupCodeVerifyRequest';
import IMfaConfirmEnrollmentRequest from 'types/auth/IMfaConfirmEnrollmentRequest';
import IMfaEnrollRequest from 'types/auth/IMfaEnrollRequest';
import IMfaEnrollmentPayload from 'types/auth/IMfaEnrollmentPayload';
import IMfaOptInResponse from 'types/auth/IMfaOptInResponse';
import IMfaVerifyRequest from 'types/auth/IMfaVerifyRequest';
import IToken from 'types/auth/IToken';

/**
 * Starts enrollment. Anonymous: the request interceptor only attaches a bearer token when
 * `authStateSelector` is true, and during enrollment there is no session, so nothing is attached.
 *
 * Every call issues a NEW secret and a NEW set of backup codes and invalidates the previous ones.
 * Call it exactly once per enrollment attempt — see `useMfaEnrollment`'s ref latch.
 */
export const enrollMfaRequest = async (mfaToken: string): Promise<IMfaEnrollmentPayload> => {
  const body: IMfaEnrollRequest = { mfaToken };
  const response: AxiosResponse<IMfaEnrollmentPayload> = await getApi().post(MFA_ENROLL_ENDPOINT, body);
  return response.data;
};

/**
 * Commits the enrollment. Returns a normal 10-hour web session token with the same claims an
 * ordinary login would have produced. The mfaToken stops working the moment this succeeds.
 */
export const confirmMfaEnrollmentRequest = async (mfaToken: string, code: string): Promise<IToken> => {
  const body: IMfaConfirmEnrollmentRequest = { mfaToken, code };
  const response: AxiosResponse<ILoginResponse> = await getApi().post(MFA_ENROLL_CONFIRM_ENDPOINT, body);
  return fromLoginResponseToToken(response.data);
};

/**
 * Answers the returning-user challenge. Anonymous for the same reason as the calls above: there is
 * no session yet, so the request interceptor attaches nothing.
 *
 * Returns the ordinary login payload — a 10-hour session token with the claims a login that never
 * saw a challenge would have produced. The mfaToken stops working the moment this succeeds, which is
 * why a second submit of the same code earns [MFA] SESSION_EXPIRED rather than a second session.
 */
export const verifyMfaChallengeRequest = async (mfaToken: string, code: string): Promise<IToken> => {
  const body: IMfaVerifyRequest = { mfaToken, code };
  const response: AxiosResponse<ILoginResponse> = await getApi().post(MFA_VERIFY_ENDPOINT, body);
  return fromLoginResponseToToken(response.data);
};

/**
 * Answers the same challenge as `verifyMfaChallengeRequest`, with one of the eight single-use
 * recovery codes from enrollment instead of a live authenticator code. Anonymous for the same
 * reason: the mfaToken *is* the credential.
 *
 * The two are alternatives on one screen, not two flows — they consume the same challenge row, so
 * whichever succeeds spends it and the other then answers [MFA] SESSION_EXPIRED. `isMobile` is
 * omitted: this is the web client, and omitting it is what asks for the ordinary 10-hour session.
 *
 * The returned token carries `remainingBackupCodes`, the count left *after* this call. It is the
 * only request in the app that populates it.
 */
export const verifyMfaBackupCodeRequest = async (mfaToken: string, code: string): Promise<IToken> => {
  const body: IMfaBackupCodeVerifyRequest = { mfaToken, code };
  const response: AxiosResponse<ILoginResponse> = await getApi().post(MFA_VERIFY_BACKUP_CODE_ENDPOINT, body);
  return fromLoginResponseToToken(response.data);
};

/**
 * Grace-period opt-in: turns the caller's ordinary session into a pending-enrollment mfaToken, which
 * the existing `enrollMfaRequest` / `confirmMfaEnrollmentRequest` pair then consumes unchanged.
 *
 * The only MFA call that is NOT anonymous — the bearer token is attached by the request interceptor
 * because the caller is signed in. Expected failures: 409 [MFA] ALREADY_ENROLLED (enrolled meanwhile,
 * e.g. in another tab) and 403 [MFA] NOT_AUTHORIZED (MFA is not enforced for them). Both mean the
 * banner flag the caller routed on is stale, so both should end in a `/Profile` refetch.
 */
export const optInMfaRequest = async (): Promise<IMfaOptInResponse> => {
  const response: AxiosResponse<IMfaOptInResponse> = await getApi().post(MFA_OPT_IN_ENDPOINT);
  return response.data;
};
